import type { Locale } from '../types'

/**
 * Admin dictionary. Kept separate from the mobile strings file for size, but
 * merged into the same `t()` lookup by LocaleContext, so there is still ONE
 * translation entry point for the whole product.
 */
export const adminStrings: Record<Locale, Record<string, string>> = {
  'pt-BR': {
    /* Chrome */
    'adm.brand': 'Admin',
    'adm.search': 'Buscar usuário, conteúdo ou denúncia',
    'adm.notifications': 'Notificações operacionais',
    'adm.menu.open': 'Abrir menu',
    'adm.menu.close': 'Fechar menu',
    'adm.menu.settings': 'Configurações',
    'adm.menu.myAudit': 'Minhas ações na auditoria',
    'adm.menu.signOut': 'Encerrar sessão',
    'adm.nav.aria': 'Navegação do admin',
    'adm.breadcrumb': 'Trilha',
    'adm.privacyNote': 'Dados de saúde agregados por padrão',
    'adm.role.superadmin': 'Super Admin',

    /* Shared actions */
    'adm.action.retry': 'Tentar novamente',
    'adm.action.cancel': 'Cancelar',
    'adm.action.confirm': 'Confirmar',
    'adm.action.close': 'Fechar',
    'adm.action.save': 'Salvar',
    'adm.action.clear': 'Limpar',
    'adm.action.columns': 'Colunas',
    'adm.action.actions': 'Ações',
    'adm.action.back': 'Voltar',

    /* Table */
    'adm.table.selected': '{n} selecionados',
    'adm.table.selectPage': 'Selecionar página',
    'adm.table.select': 'Selecionar {id}',
    'adm.table.empty': 'Nenhum registro',
    'adm.table.emptyBody': 'Ajuste os filtros ou a busca para ver resultados.',
    'adm.table.records': '{total} registros · página {page} de {pages}',
    'adm.table.prev': 'Página anterior',
    'adm.table.next': 'Próxima página',

    /* Ranges */
    'adm.range.7d': '7 dias',
    'adm.range.30d': '30 dias',
    'adm.range.90d': '90 dias',
    'adm.range.12m': '12 meses',

    /* Confirmation */
    'adm.confirm.type': 'Digite {word} para confirmar',

    /* Permission */
    'adm.permission.title': 'Acesso não autorizado',
    'adm.permission.body':
      'Seu papel não tem permissão para {area}. A autorização é validada no backend, não apenas na interface.',

    /* Login */
    'adm.login.headline': 'Operação, moderação e análise da plataforma.',
    'adm.login.body':
      'Este acesso é restrito à equipe BEELYV e registrado na auditoria. Dados de saúde são tratados de forma agregada por padrão.',
    'adm.login.p1': 'Autenticação separada do aplicativo de consumo',
    'adm.login.p2': 'Segundo fator obrigatório para papéis administrativos',
    'adm.login.p3': 'Toda ação relevante fica registrada com autor e horário',
    'adm.login.title': 'Acesso administrativo',
    'adm.login.subtitle':
      'Use as credenciais da sua conta de equipe. Este acesso não usa o login do aplicativo.',
    'adm.login.expired':
      'Sua sessão expirou por inatividade. Nenhum dado foi perdido — entre novamente para continuar.',
    'adm.login.email': 'E-mail',
    'adm.login.password': 'Senha',
    'adm.login.invalid':
      'Credenciais inválidas. Verifique o e-mail e a senha — após 5 tentativas o acesso é bloqueado temporariamente.',
    'adm.login.continue': 'Continuar',
    'adm.login.forgot': 'Esqueci minha senha',
    'adm.2fa.title': 'Verificação em duas etapas',
    'adm.2fa.body':
      'Digite o código de 6 dígitos do seu aplicativo autenticador. O segundo fator é obrigatório para contas administrativas.',
    'adm.2fa.code': 'Código',
    'adm.2fa.confirm': 'Confirmar e entrar',

    /* State gallery */
    'adm.states.title': 'Galeria de estados',
    'adm.states.subtitle':
      'Referência interna do design system. Não aparece na navegação de produção.',
    'adm.states.loading': 'Carregando',
    'adm.states.skeleton': 'Esqueleto de tabela',
    'adm.states.empty': 'Vazio',
    'adm.states.noResults': 'Sem resultados',
    'adm.states.error': 'Erro',
    'adm.states.permission': 'Permissão negada',
    'adm.states.session': 'Sessão expirada',
    'adm.states.exportProcessing': 'Exportação processando',
    'adm.states.exportDone': 'Exportação concluída',
    'adm.states.success': 'Sucesso',
    'adm.states.destructive': 'Confirmação destrutiva',
    'adm.states.openModal': 'Abrir confirmação',
    'adm.states.usage': 'Onde é usado',
  },

  'en-US': {
    'adm.brand': 'Admin',
    'adm.search': 'Search a user, content or report',
    'adm.notifications': 'Operational notifications',
    'adm.menu.open': 'Open menu',
    'adm.menu.close': 'Close menu',
    'adm.menu.settings': 'Settings',
    'adm.menu.myAudit': 'My actions in the audit log',
    'adm.menu.signOut': 'Sign out',
    'adm.nav.aria': 'Admin navigation',
    'adm.breadcrumb': 'Breadcrumb',
    'adm.privacyNote': 'Health data aggregated by default',
    'adm.role.superadmin': 'Super Admin',

    'adm.action.retry': 'Try again',
    'adm.action.cancel': 'Cancel',
    'adm.action.confirm': 'Confirm',
    'adm.action.close': 'Close',
    'adm.action.save': 'Save',
    'adm.action.clear': 'Clear',
    'adm.action.columns': 'Columns',
    'adm.action.actions': 'Actions',
    'adm.action.back': 'Back',

    'adm.table.selected': '{n} selected',
    'adm.table.selectPage': 'Select page',
    'adm.table.select': 'Select {id}',
    'adm.table.empty': 'No records',
    'adm.table.emptyBody': 'Adjust the filters or the search to see results.',
    'adm.table.records': '{total} records · page {page} of {pages}',
    'adm.table.prev': 'Previous page',
    'adm.table.next': 'Next page',

    'adm.range.7d': '7 days',
    'adm.range.30d': '30 days',
    'adm.range.90d': '90 days',
    'adm.range.12m': '12 months',

    'adm.confirm.type': 'Type {word} to confirm',

    'adm.permission.title': 'Access not authorized',
    'adm.permission.body':
      'Your role is not allowed to {area}. Authorization is enforced in the backend, not only in the interface.',

    'adm.login.headline': 'Platform operations, moderation and analytics.',
    'adm.login.body':
      'This access is restricted to the Beelyv team and recorded in the audit log. Health data is handled in aggregate by default.',
    'adm.login.p1': 'Authentication separate from the consumer app',
    'adm.login.p2': 'Second factor required for administrative roles',
    'adm.login.p3': 'Every relevant action is recorded with author and timestamp',
    'adm.login.title': 'Administrative access',
    'adm.login.subtitle':
      'Use your team account credentials. This access does not use the app login.',
    'adm.login.expired':
      'Your session expired due to inactivity. No data was lost — sign in again to continue.',
    'adm.login.email': 'Email',
    'adm.login.password': 'Password',
    'adm.login.invalid':
      'Invalid credentials. Check the email and password — after 5 attempts access is temporarily blocked.',
    'adm.login.continue': 'Continue',
    'adm.login.forgot': 'I forgot my password',
    'adm.2fa.title': 'Two-factor verification',
    'adm.2fa.body':
      'Enter the 6-digit code from your authenticator app. The second factor is required for administrative accounts.',
    'adm.2fa.code': 'Code',
    'adm.2fa.confirm': 'Confirm and sign in',

    'adm.states.title': 'State gallery',
    'adm.states.subtitle':
      'Internal design-system reference. It does not appear in production navigation.',
    'adm.states.loading': 'Loading',
    'adm.states.skeleton': 'Table skeleton',
    'adm.states.empty': 'Empty',
    'adm.states.noResults': 'No results',
    'adm.states.error': 'Error',
    'adm.states.permission': 'Permission denied',
    'adm.states.session': 'Session expired',
    'adm.states.exportProcessing': 'Export processing',
    'adm.states.exportDone': 'Export completed',
    'adm.states.success': 'Success',
    'adm.states.destructive': 'Destructive confirmation',
    'adm.states.openModal': 'Open confirmation',
    'adm.states.usage': 'Where it is used',
  },
}
